import React, { Suspense, useEffect } from 'react';
import { Navigate, Routes, useLocation, useNavigate, Route } from 'react-router-dom';
import Spinner from '../../components/Spinner/Spinner';
import Header from '../../components/Header/Header';
import MenuDetailsContainer from '../../containers/menu/menuDetails';
import RequireAuth from '../../components/RequireAuth/RequireAuth';
import * as CONSTANT from '../../constants';
import {
  getUser,
  getVisitorId,
  shouldRelease,
} from '../../helpers/helperMethods';
import HelmetWrapper from '../../components/ReactHelmetHOC/ReactHelmetWrapper';
import { bugSnagInititalize } from '../../helpers/bugSnagHelper';
import { useAuthState } from '../../context/UserAuthentication';
import { useAppDispatch, useAppSelector } from '../../redux/store/store';
import { resetLocationFlow } from '../../redux/slices/itemSlice';
import MoreDevices from '../../components/Tabs/AuthenticatedDevices/MoreDevices';
import RequireAuthAndCart from '../../components/RequireAuthAndCart/RequireAuthAndCart';

const ErrorBoundaryBugSnag = bugSnagInititalize();

const HomeContainer = React.lazy(() => import('../home/home'));
const PublicOrderTrackingContainer = React.lazy(() => import('../publicOrderTracking/publicOrderTracking'));
const OurKitchenContainer = React.lazy(() => import('../ourKitchen/ourKitchen'));
const goodLifeContainer = React.lazy(() => import('../../containers/goodLife/GoodLife'));
const greekGiveAwayTermsContainer = React.lazy(() => import('../../containers/goodLife/GoodLifeTerms'));
const FundraisingContainer = React.lazy(() => import('../fundraising/Fundraiser'));
const LocationInfoContainer = React.lazy(() => import('../location/locationInfo'));
const locationDetails = React.lazy(() => import('../location/locationDetails/locationDetails'));
const ItemGridContainer = React.lazy(() => import('../../components/ItemGrid/ItemGrid'));
const OrderContainer = React.lazy(() => import('../../containers/order/order'));
const CustomizeContainer = React.lazy(() => import('../../containers/order/customize'));
const AccountContainer = React.lazy(() => import('../../containers/account/accountDetails'));
const LoginContainer = React.lazy(() => import('../account/login'));
const CreateAccountContainer = React.lazy(() => import('../account/createAccount'));
const ForgotPasswordContainer = React.lazy(() => import('../account/forgotPassword'));
const NewPasswordContainer = React.lazy(() => import('../account/newPassword'));
const ProfileDetailsContainer = React.lazy(() => import('../account/profileDetails'));
const ScanContainer = React.lazy(() => import('../../components/UserSelections/Scan'))
const CheckoutContainer = React.lazy(() => import('../../containers/order/checkout'));
const CheckoutCurbsideContainer = React.lazy(() => import('../order/checkoutCurbside'));
const OrderConfirmationContainer = React.lazy(() => import('../../containers/order/orderConfirmation'));
const OrderConfirmationDeliveryContainer = React.lazy(() => import('../../containers/order/orderConfirmationDelivery'));
const OrderConfirmationGuestContainer = React.lazy(() => import('../../containers/order/orderConfirmationGuest'));
const AddIngredients = React.lazy(() => import('../../containers/order/ingredients'));
const OrderTracking = React.lazy(() => import('../../containers/orderTracking/index'));
const CareerContainer = React.lazy(() => import('../career/career'));
const CareerValues = React.lazy(() => import('../career/careerValues'));
const TravelAbroad = React.lazy(() => import('../career/travelAboard'));
const Hospitality = React.lazy(() => import('../career/hospitalityTeam'));
const Culinary = React.lazy(() => import('../career/culinaryTeam'));
const Leadership = React.lazy(() => import('../career/leadershipTeam'));
const Catering = React.lazy(() => import('../career/cateringTeam'));
const ContactUsContainer = React.lazy(() => import('../talkToUs/ContactUs'));
const LocationContainer = React.lazy(() => import('../location/location'));
const ViewAllLocations = React.lazy(() => import('../location/ViewAllLocation'));
const MenuContainer = React.lazy(() => import('../menu/menu'));
const Terms = React.lazy(() => import('../terms/terms'));
const PrivacyPolicy = React.lazy(() => import('../privacyPolicy/privacyPolicy'));
const DownloadApp = React.lazy(() => import('../downloadApp/downloadApp'));
const KidsCombo = React.lazy(() => import('../kidsCombo'));
const WelcomeScreen = React.lazy(() => import('../account/welcomeScreen'));
const RewardsRedeem = React.lazy(() => import('../rewardsDetails/RedeemRewards'));
const ViewMoreRecentOrder = React.lazy(() => import('../../components/Tabs/RecentOrder/ViewMoreRecentOrder'));
const PrintRecipt = React.lazy(() => import('../../components/Tabs/RecentOrder/PrintRecipt/PrintRecipt'));
const LoyaltyFAQ = React.lazy(() => import('../loyaltyFAQ/loyaltyFAQ'));
const Kiosk = React.lazy(() => import('../kiosk/Kiosk'));
const Page404 = React.lazy(() => import('../../components/404page/Page404'));
const GoodLifeLane = React.lazy(() => import('../../components/GoodLifeLane/GoodLifeLane'));
const Historypage = React.lazy(() => import('../../containers/history/History'));

const AppRoutes: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const LocationFlow = useAppSelector((state) => state.Items.LocationFlow);
  const { authInfo: user } = useAuthState();

  const checkVisitorId = () => {
    if (!getVisitorId() && !getUser().id) navigate(0);
  };
  
  useEffect(() => {
    const handleFocus = () => checkVisitorId();
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [navigate]);

  const locationRelatedPage = location.pathname.includes('location');

  if (
    !locationRelatedPage &&
    !LocationFlow?.locationModalOpen &&
    LocationFlow?.intendedPath
  ) {
    dispatch(resetLocationFlow());
  }

  return (
    <Suspense fallback={<Spinner lightBg={true} />}>
      <Header />
      <ErrorBoundaryBugSnag FallbackComponent={Page404}>
          <Routes>
            <Route path={CONSTANT.ROUTE_HOME} element={<HelmetWrapper Component={HomeContainer} />} />
            <Route path={CONSTANT.CATERING_CAFE_ZUPAS_KIOSK} element={<HelmetWrapper Component={Kiosk} />} />
            <Route path={CONSTANT.ROUTE_FAQ} element={<HelmetWrapper Component={LoyaltyFAQ} />} />
            <Route path={CONSTANT.ROUTE_OUR_KITCHEN} element={<HelmetWrapper Component={OurKitchenContainer} />} />
            <Route path={CONSTANT.ROUTE_FUNDRAISING} element={<HelmetWrapper Component={FundraisingContainer} />} />
            <Route path={CONSTANT.ROUTE_CAREERS} element={<HelmetWrapper Component={CareerContainer} />} />
            <Route path={CONSTANT.ROUTE_CAREERS_VALUES} element={<HelmetWrapper Component={CareerValues} />} />
            <Route path={CONSTANT.ROUTE_TRAVEL_ABROAD} element={<HelmetWrapper Component={TravelAbroad} />} />
            <Route path={CONSTANT.ROUTE_HOSPITALITY_TEAM} element={<HelmetWrapper Component={Hospitality} />} />
            <Route path={CONSTANT.ROUTE_CULINARY_TEAM} element={<HelmetWrapper Component={Culinary} />} />
            <Route path={CONSTANT.ROUTE_LEADERSHIP_TEAM} element={<HelmetWrapper Component={Leadership} />} />
            <Route path={CONSTANT.ROUTE_CATERING_TEAM} element={<HelmetWrapper Component={Catering} />} />
            <Route path={CONSTANT.ROUTE_TALK_TO_US} element={<HelmetWrapper Component={ContactUsContainer} />} />
            <Route path={CONSTANT.ROUTE_LOCATIONS} element={<HelmetWrapper Component={LocationContainer} />} />
            <Route path={CONSTANT.ROUTE_VIEW_ALL_LOCATIONS} element={<HelmetWrapper Component={ViewAllLocations} />} />
            <Route path={CONSTANT.ROUTE_LOCATIONS_INFO} element={<HelmetWrapper Component={LocationInfoContainer} />} />
            <Route path={`${CONSTANT.ROUTE_LOCATION_INFO}/:id`} element={<HelmetWrapper Component={locationDetails} />} />
            <Route path={CONSTANT.ROUTE_MENU} element={<HelmetWrapper Component={MenuContainer} />} />
            <Route path={`${CONSTANT.ROUTE_MENU}/:selected_category?`} element={<HelmetWrapper Component={MenuContainer} />} />
            <Route path={`${CONSTANT.ROUTE_MENU}/:id`} element={<HelmetWrapper Component={MenuContainer} />} />
            <Route path={CONSTANT.TERMS_ROUTE} element={<HelmetWrapper Component={Terms} />} />
            <Route path={CONSTANT.TERMS_ROUTE_HTML} element={<HelmetWrapper Component={Terms} />} />
            <Route path={CONSTANT.PRIVACY_POLICY_ROUTE} element={<HelmetWrapper Component={PrivacyPolicy} />} />
            <Route path={CONSTANT.DOWNLOAD_APP} element={<HelmetWrapper Component={DownloadApp} />} />
            <Route path={`${CONSTANT.ROUTE_MENU_DETAILS}/:id`} element={<HelmetWrapper Component={MenuDetailsContainer} />} />
            <Route path={`${CONSTANT.ROUTE_CATERING_MENU_DETAILS}/:id`} element={<HelmetWrapper Component={MenuDetailsContainer} />} />
            <Route path={CONSTANT.ROUTE_Item_Grid} element={<HelmetWrapper Component={ItemGridContainer} />} />
            <Route path={`${CONSTANT.ROUTE_ORDER}/:id`} element={<HelmetWrapper Component={OrderContainer} />} />
            <Route path={`${CONSTANT.ROUTE_CUSTOMIZE}/:id`} element={<HelmetWrapper Component={CustomizeContainer} />} />
            <Route path={CONSTANT.ROUTE_LOGIN} element={<HelmetWrapper Component={LoginContainer} />} />
            <Route path={CONSTANT.ROUTE_ACCOUNT} element={<HelmetWrapper Component={AccountContainer} />} />
            <Route path={CONSTANT.ROUTE_CREATE_ACCOUNT} element={<HelmetWrapper Component={CreateAccountContainer} />} />
            <Route path={CONSTANT.ROUTE_FORGOT_PASSWORD} element={<HelmetWrapper Component={ForgotPasswordContainer} />} />
            <Route path={CONSTANT.ROUTE_NEW_PASSWORD} element={<HelmetWrapper Component={NewPasswordContainer} />} />

            {/* ✅ Protected Routes with wrapper inside */}
            <Route path={CONSTANT.ROUTE_PROFILE_DETAILS} element={<RequireAuth Component={ProfileDetailsContainer} />} />
            <Route path={CONSTANT.VIEW_MORE_RECENT_ORDER} element={<RequireAuth Component={ViewMoreRecentOrder} />} />
            <Route path={CONSTANT.VIEW_MORE_RECENT_FAVORITE_ORDER} element={<RequireAuth Component={ViewMoreRecentOrder} />} />
            <Route path={CONSTANT.ROUTE_THANK_YOU} element={<RequireAuth Component={OrderTracking} />} />
            <Route path={CONSTANT.ROUTE_PROFILE_DEVICES} element={<RequireAuth Component={MoreDevices} />} />
            <Route path={CONSTANT.ROUTE_CHECKOUT} element={<RequireAuthAndCart Component={CheckoutContainer} />} />
            <Route path={CONSTANT.ROUTE_SCAN} element={<RequireAuth Component={ScanContainer} />} />
            <Route path={CONSTANT.ROUTE_CHECKOUT_CURBSIDE} element={<HelmetWrapper Component={CheckoutCurbsideContainer} />} />
            <Route path={CONSTANT.ROUTE_ADD_INGREDIENTS} element={<HelmetWrapper Component={AddIngredients} />} />
            <Route path={CONSTANT.ROUTE_GOOD_LIFE} element={<HelmetWrapper Component={goodLifeContainer} />} />
            <Route path={CONSTANT.ROUTE_GREEK_GIVEAWAY_TERMS} element={<HelmetWrapper Component={greekGiveAwayTermsContainer} />} />
            <Route path={CONSTANT.ROUTE_ORDER_CONFIRMATION} element={<HelmetWrapper Component={OrderConfirmationContainer} />} />
            <Route path={CONSTANT.ROUTE_ORDER_CONFIRMATION_DELIVERY} element={<HelmetWrapper Component={OrderConfirmationDeliveryContainer} />} />
            <Route path={CONSTANT.ROUTE_ORDER_CONFIRMATION_GUEST} element={<HelmetWrapper Component={OrderConfirmationGuestContainer} />} />
            <Route path={CONSTANT.ROUTE_KIDS_COMBO} element={<HelmetWrapper Component={KidsCombo} />} />
            <Route path={CONSTANT.ROUTE_WELCOME_SCREEN} element={<HelmetWrapper Component={WelcomeScreen} />} />

            {shouldRelease('Routes', 'redeem') && (
            <Route path={CONSTANT.ROUTE_REWARDS_REDEEM} element={<RequireAuth Component={RewardsRedeem} />} />  
            )}

            <Route path={CONSTANT.PRINT_RECEIPT} element={<HelmetWrapper Component={PrintRecipt} />} />
            <Route path={`${CONSTANT.ROUTE_ORDER_TRACKING}/:id`} element={<HelmetWrapper Component={PublicOrderTrackingContainer} />} />
            <Route path={CONSTANT.ROUTE_GOOD_LIFE_LANE} element={<HelmetWrapper Component={GoodLifeLane} />} />
            <Route path={CONSTANT.ROUTE_HISTORY} element={<HelmetWrapper Component={Historypage} />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/menu" replace />} />
          </Routes>
      </ErrorBoundaryBugSnag>
    </Suspense>
  );
};

export default AppRoutes;
